import { useState } from 'react'
import { supabase } from '../../../../lib/supabase'

export default function ChapterCard({ chapter, onRefresh }) {
  const memberCount = chapter.supporters?.[0]?.count || 0
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: chapter.name,
    region: chapter.region || '',
    chairperson_name: chapter.chairperson_name || '',
    chairperson_photo: chapter.chairperson_photo || '',
    vice_name: chapter.vice_name || '',
    vice_photo: chapter.vice_photo || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleEdit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const { error } = await supabase
      .from('chapters')
      .update({
        name: formData.name,
        region: formData.region,
        chairperson_name: formData.chairperson_name,
        chairperson_photo: formData.chairperson_photo,
        vice_name: formData.vice_name,
        vice_photo: formData.vice_photo,
      })
      .eq('id', chapter.id)
    if (error) { setError(error.message); setSaving(false); return }
    setSaving(false)
    setEditing(false)
    onRefresh()
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${chapter.name}"? This cannot be undone.`)) return
    // Remove chapter reference from all supporters first to avoid FK constraint
    await supabase.from('supporters').update({ chapter_id: null }).eq('chapter_id', chapter.id)
    await supabase.from('chapters').delete().eq('id', chapter.id)
    onRefresh()
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors w-full"

  if (editing) {
    return (
      <div className="bg-gray-100 dark:bg-white/5 border border-green-500/50 dark:border-green-600/50 p-6">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">Edit Chapter</div>
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-3 py-2 text-xs mb-4">{error}</div>
        )}
        <form onSubmit={handleEdit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Chapter Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Region</label>
            <input
              type="text"
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              required
              className={inputClass}
            />
          </div>

          <div className="border-t border-gray-200 dark:border-white/10 pt-3 mt-1">
            <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-3">Chapter Leadership</div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Chairperson Name</label>
                <input
                  type="text"
                  value={formData.chairperson_name}
                  onChange={(e) => setFormData({ ...formData, chairperson_name: e.target.value })}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Chairperson Photo URL</label>
                <input
                  type="text"
                  value={formData.chairperson_photo}
                  onChange={(e) => setFormData({ ...formData, chairperson_photo: e.target.value })}
                  placeholder="https://... or leave blank"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Vice Chairperson Name</label>
                <input
                  type="text"
                  value={formData.vice_name}
                  onChange={(e) => setFormData({ ...formData, vice_name: e.target.value })}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Vice Photo URL</label>
                <input
                  type="text"
                  value={formData.vice_photo}
                  onChange={(e) => setFormData({ ...formData, vice_photo: e.target.value })}
                  placeholder="https://... or leave blank"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-2 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 hover:border-green-500/50 dark:hover:border-green-900/50 transition-colors">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className="font-black text-lg uppercase tracking-tight truncate text-gray-900 dark:text-white">
            {chapter.name}
          </h3>
          <div className="text-gray-500 text-xs mt-1 uppercase tracking-widest">
            {chapter.region} Region
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-bold uppercase tracking-widest transition-colors"
          >
            Edit
          </button>
          <span className="text-gray-300 dark:text-white/20">|</span>
          <button
            onClick={handleDelete}
            className="text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-white/10 pt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Members</div>
          <div className="font-black text-2xl text-gray-900 dark:text-white">{memberCount}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Created</div>
          <div className="text-sm text-gray-500">
            {new Date(chapter.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>

      {(chapter.chairperson_name || chapter.vice_name) && (
        <div className="border-t border-gray-200 dark:border-white/10 pt-4 mt-4 flex flex-col gap-3">
          <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold">Leadership</div>
          <div className="flex gap-3">
            {chapter.chairperson_name && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-900/50 border border-green-700/40 overflow-hidden flex items-center justify-center shrink-0">
                  {chapter.chairperson_photo
                    ? <img src={chapter.chairperson_photo} alt={chapter.chairperson_name} className="w-full h-full object-cover" />
                    : <span className="text-white text-xs font-black">{chapter.chairperson_name.split(' ').map(n => n[0]).join('').slice(0,2)}</span>
                  }
                </div>
                <div>
                  <div className="text-xs text-green-600 dark:text-green-400 uppercase tracking-widest font-bold">Chair</div>
                  <div className="text-xs font-bold text-gray-900 dark:text-white">{chapter.chairperson_name}</div>
                </div>
              </div>
            )}
            {chapter.vice_name && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-900/30 border border-green-800/30 overflow-hidden flex items-center justify-center shrink-0">
                  {chapter.vice_photo
                    ? <img src={chapter.vice_photo} alt={chapter.vice_name} className="w-full h-full object-cover" />
                    : <span className="text-white text-xs font-black">{chapter.vice_name.split(' ').map(n => n[0]).join('').slice(0,2)}</span>
                  }
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest font-bold">Vice</div>
                  <div className="text-xs font-bold text-gray-900 dark:text-white">{chapter.vice_name}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}